<template>
  <ul class="folder-tree" :class="{ root: level === 0 }">
    <li>
      <div
        class="folder-node"
        :class="{ active: folder.id === activeFolderId, entity: isEntityFolder(folder) }"
      >
        <button
          v-if="hasChildren"
          type="button"
          class="expand-btn"
          :title="expanded ? 'Collapse' : 'Expand'"
          @click.stop="toggleExpanded"
        >
          <i class="bi" :class="expanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
        </button>
        <span v-else class="expand-spacer"></span>

        <button
          type="button"
          class="folder-btn"
          :style="{ paddingLeft: (level * 10) + 'px' }"
          @click="onEnterFolder(folder)"
        >
          <i class="bi" :class="isEntityFolder(folder) ? 'bi-diagram-3-fill' : 'bi-folder-fill'"></i>
          <span class="folder-name">{{ folder.name }}</span>
          <span v-if="isEntityFolder(folder)" class="entity-tag">{{ folder.display_entity_label || entityTypeLabel(folder.entity_type) }}</span>
        </button>
      </div>

      <template v-if="expanded && hasChildren">
        <FolderTree
          v-for="child in folder.children"
          :key="child.id"
          :folder="child"
          :level="level + 1"
          :activeFolderId="activeFolderId"
          @enterFolder="onEnterFolder"
        />
      </template>
    </li>
  </ul>
</template>

<script>
export default {
  name: "FolderTree",
  props: {
    folder: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      default: 0,
    },
    activeFolderId: {
      type: [Number, String, null],
      default: null,
    },
  },
  data() {
    return {
      expanded: this.level === 0,
    };
  },
  computed: {
    hasChildren() {
      return Array.isArray(this.folder?.children) && this.folder.children.length > 0;
    },
  },
  watch: {
    activeFolderId: {
      immediate: true,
      handler(newVal) {
        if (!newVal || !this.hasChildren) return;
        if (String(this.folder?.id) === String(newVal) || this.containsActiveDescendant(this.folder, newVal)) {
          this.expanded = true;
        }
      },
    },
  },
  methods: {
    isEntityFolder(folder) {
      if (!folder?.entity_type || folder?.entity_id == null) return false;
      return String(folder.entity_type).toLowerCase() !== 'project';
    },
    entityTypeLabel(type) {
      const normalized = String(type || '').trim();
      const map = {
        action: 'Activity',
        workflow: 'Workflow',
        experiment: 'Experiment',
        project_structure_element: 'Structure Element',
        structure_element: 'Structure Element',
      };
      return map[normalized] || (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '');
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    containsActiveDescendant(node, activeId) {
      if (!node || !Array.isArray(node.children)) return false;
      return node.children.some((child) => {
        if (String(child.id) === String(activeId)) return true;
        return this.containsActiveDescendant(child, activeId);
      });
    },
    onEnterFolder(folder) {
      this.$emit("enterFolder", folder);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.folder-tree {
  list-style: none;
  margin: 0;
  padding: 0;
}

.folder-tree.root {
  margin-top: 4px;
}

.folder-node {
  display: flex;
  align-items: center;
  min-height: 28px;
  border-radius: 6px;
}

.folder-node.active {
  background: rgba($primary-color, 0.12);
}

.folder-node:hover {
  background: #fff;
}

.expand-btn,
.expand-spacer {
  width: 20px;
  min-width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.expand-btn {
  border: none;
  background: transparent;
  color: $primary-color;
  cursor: pointer;
  padding: 0;
}

.folder-btn {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $primary-color;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 2px 4px;
  text-align: left;
}

.folder-btn:hover {
  background: #fff;
}

.folder-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-tag {
  margin-left: auto;
  font-size: 10px;
  text-transform: uppercase;
  color: $primary-color;
  background-color: rgba($primary-color, 0.12);
  border-radius: 10px;
  padding: 1px 6px;
}
</style>
  